import { discountPrice } from 'src/shared/frontendTypes/product'
import { priceLock } from 'src/shared/frontendTypes/warenkorb'
import {
  applyQuantityDiscountToLinePrice,
  isQuantityDiscountAvailable,
} from 'src/shared/discountShop'

export const CART_PRICE_LOCK_DAYS = 7

export type cartPrice = number | discountPrice
type CartPriceOptions = {
  ignoreQuantityDiscount?: boolean
  onlyStock?: boolean | null
  stock?: number | null
  now?: Date
}

const normalizeQty = (qty?: number) => Math.max(0, Math.floor(qty ?? 1))
const normalizeUnitQty = (qty?: number) => Math.max(1, normalizeQty(qty))
const getQuantityDiscount = (
  price: discountPrice,
  options?: CartPriceOptions,
) => {
  if (options?.ignoreQuantityDiscount) return undefined

  return isQuantityDiscountAvailable(price.quantityDiscount, {
    onlyStock: options?.onlyStock,
    stock: options?.stock,
    dateEnd: price.dateEnd,
    now: options?.now,
  })
    ? price.quantityDiscount
    : undefined
}

export const getCartLinePrice = (
  price: cartPrice,
  qty = 1,
  options?: CartPriceOptions,
) => {
  const safeQty = normalizeQty(qty)
  if (typeof price === 'number') return price * safeQty

  const stockDiscountLimit = typeof price.stockDiscountLimit === 'number'
    ? Math.max(0, Math.floor(price.stockDiscountLimit))
    : 0
  const hasStockLimitedPrice = price.limitStockDiscountToStock === true
    && stockDiscountLimit > 0
    && typeof price.discountPriceAfterStock === 'number'

  if (!hasStockLimitedPrice) {
    return applyQuantityDiscountToLinePrice(
      Array(safeQty).fill(price.discountPrice),
      getQuantityDiscount(price, options),
    )
  }

  const discountPriceAfterStock = price.discountPriceAfterStock
  if (typeof discountPriceAfterStock !== 'number') {
    return applyQuantityDiscountToLinePrice(
      Array(safeQty).fill(price.discountPrice),
      getQuantityDiscount(price, options),
    )
  }

  const stockDiscountQty = Math.min(safeQty, stockDiscountLimit)
  const afterStockQty = safeQty - stockDiscountQty

  return applyQuantityDiscountToLinePrice(
    [
      ...Array(stockDiscountQty).fill(price.discountPrice),
      ...Array(afterStockQty).fill(discountPriceAfterStock),
    ],
    getQuantityDiscount(price, options),
  )
}

export const getCartPriceAmount = (
  price: cartPrice,
  qty = 1,
  options?: CartPriceOptions,
) => getCartLinePrice(price, qty, options) / normalizeUnitQty(qty)

export const isCartPriceLockValid = (
  lock: priceLock | undefined,
  now = new Date(),
) => {
  if (!lock || !lock.hash) return false

  const expiresAt = Date.parse(lock.expiresAt)

  return Number.isFinite(expiresAt) && expiresAt >= now.getTime()
}

export const getLowerCartPrice = (
  priceA: cartPrice,
  priceB: cartPrice,
): cartPrice =>
  getCartPriceAmount(priceA) <= getCartPriceAmount(priceB)
    ? priceA
    : priceB
